import type { JSX } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { formatMonthLabel } from '@/lib/date-input';
import { cn } from '@/lib/utils';

interface PageHeadProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** `page-tools` : à droite du titre (référence + copie, badge de statut). */
  tools?: ReactNode;
  /** Titre en mono (référence technique). */
  mono?: boolean;
  className?: string;
}

/**
 * `page-head` : titre h1 (style `title`, 20px) + sous-titre 13px, outils à
 * droite. Les actions de page (bouton principal, sélecteur de mois) vont dans
 * la barre du haut via `<PageChrome actions>`, pas ici.
 */
export function PageHead({
  title,
  subtitle,
  tools,
  mono = false,
  className,
}: PageHeadProps): JSX.Element {
  return (
    <div className={cn('flex flex-wrap items-end justify-between gap-4', className)}>
      <div className="min-w-0">
        <h1 className={cn('m-0 text-title', mono && 'font-mono')}>{title}</h1>
        {subtitle !== undefined && subtitle !== null && subtitle !== '' ? (
          <p className="m-0 mt-0.5 text-[13px] leading-[18px] text-muted-foreground">{subtitle}</p>
        ) : null}
      </div>
      {tools !== undefined && tools !== null ? (
        <div className="flex flex-wrap items-center gap-2">{tools}</div>
      ) : null}
    </div>
  );
}

/** Décale un mois `YYYY-MM` ; renvoie la valeur reçue si elle est mal formée. */
export function shiftMonth(month: string, delta: number): string {
  const [year, monthNumber] = month.split('-').map(Number);
  if (
    year === undefined ||
    monthNumber === undefined ||
    Number.isNaN(year) ||
    Number.isNaN(monthNumber)
  ) {
    return month;
  }
  const index = year * 12 + (monthNumber - 1) + delta;
  const nextYear = Math.floor(index / 12);
  const nextMonth = (index % 12) + 1;
  return `${nextYear}-${String(nextMonth).padStart(2, '0')}`;
}

/** « octobre 2026 » pour `2026-10` (défini avec les autres formats de date français). */
export { formatMonthLabel };

/**
 * Sélecteur de mois (`month` de la barre du haut) : ‹ [calendrier octobre 2026] ›,
 * boutons `outline` 28px. Valeur au format `YYYY-MM`. À placer dans les
 * `actions` de `<PageChrome>`.
 */
export function MonthSwitcher({
  month,
  onChange,
}: {
  month: string;
  onChange: (month: string) => void;
}): JSX.Element {
  return (
    <div className="inline-flex items-center gap-1" role="group" aria-label="Mois affiché">
      <Button
        variant="outline"
        size="icon-sm"
        aria-label="Mois précédent"
        onClick={() => {
          onChange(shiftMonth(month, -1));
        }}
      >
        <ChevronLeft aria-hidden="true" />
      </Button>
      <span
        aria-live="polite"
        className="inline-flex h-7 min-w-[132px] items-center justify-center gap-2 rounded-md border border-input bg-background px-2 text-[13px] font-medium"
      >
        <Calendar className="size-4" aria-hidden="true" />
        {formatMonthLabel(month)}
      </span>
      <Button
        variant="outline"
        size="icon-sm"
        aria-label="Mois suivant"
        onClick={() => {
          onChange(shiftMonth(month, 1));
        }}
      >
        <ChevronRight aria-hidden="true" />
      </Button>
    </div>
  );
}
